import React, { useEffect, useState } from "react";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { EnvToggle } from "../OrgUsageAnalytics/components/EnvToggle";
import { useEnv } from "../OrgUsageAnalytics/useEnv";
import { DetailPanel } from "./components/DetailPanel";
import { EncompassOrgList } from "./components/EncompassOrgList";
import { EncompassPanel } from "./components/EncompassPanel";
import { FleetTable } from "./components/FleetTable";
import "../OrgUsageAnalytics/OrgUsageAnalytics.css";
import "./Integrations.css";

// Integration selector tabs. Each one owns its own workspace below; the two
// present today answer very different questions, which is why they do not share
// a panel. LendingPad decides which loans are imported at all. Encompass
// imports everything its feed delivers and decides who is shown which.
const INTEGRATIONS = [
  {
    id: "lendingpad",
    name: "LendingPad",
    img: "/assets/integration/lendingpad.png",
    available: true,
    title: "LendingPad",
    subtitle:
      "Connect a mortgage organization to LendingPad, check its health, and keep loan data in sync — all without needing developer access.",
  },
  {
    id: "encompass",
    name: "Encompass",
    img: null,
    available: true,
    title: "Encompass",
    subtitle:
      "Every loan Encompass delivers is synced. Decide who in each organization is shown which of them.",
  },
];

const RECENTS_KEY_PREFIX = "lendingpad-ops-recents";
const RECENTS_LIMIT = 6;

// Recents are scoped per environment so Prod and Dev never mix. LendingPad
// keeps its original key so existing recents survive; every other integration
// is namespaced, so an Encompass org can never surface in the LendingPad list.
const recentsKey = (env, integration) =>
  integration === "lendingpad"
    ? `${RECENTS_KEY_PREFIX}-${env || "prod"}`
    : `${integration}-ops-recents-${env || "prod"}`;

function loadRecents(env, integration) {
  try {
    const raw = window.localStorage.getItem(recentsKey(env, integration));
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch (_error) {
    return [];
  }
}

function IntegrationsPage({ onReady }) {
  const [env, setEnv] = useEnv();
  const [selectedIntegration, setSelectedIntegration] = useState("lendingpad");
  const [selectedOrgId, setSelectedOrgId] = useState(null);
  const [selectedCompanyId, setSelectedCompanyId] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [recents, setRecents] = useState(() => loadRecents(env, "lendingpad"));

  const integration =
    INTEGRATIONS.find((entry) => entry.id === selectedIntegration) ||
    INTEGRATIONS[0];

  useEffect(() => {
    if (onReady) {
      onReady();
    }
  }, [onReady]);

  // On environment or integration change, load that list's recents and drop the
  // selection: an org selected in one never belongs in the other, and a Prod org
  // must never show while viewing Dev.
  useEffect(() => {
    setRecents(loadRecents(env, selectedIntegration));
    setSelectedOrgId(null);
    setSelectedCompanyId(null);
  }, [env, selectedIntegration]);

  const rememberRecent = (org) => {
    if (!org?.id) {
      return;
    }
    setRecents((current) => {
      const entry = {
        id: org.id,
        name: org.name || org.id,
        effectiveState: org.effectiveState || "pending",
        image: org.image || null,
      };
      const next = [
        entry,
        ...current.filter((item) => item.id !== org.id),
      ].slice(0, RECENTS_LIMIT);
      try {
        window.localStorage.setItem(
          recentsKey(env, selectedIntegration),
          JSON.stringify(next),
        );
      } catch (_error) {
        /* ignore storage failures */
      }
      return next;
    });
  };

  const handleSelectOrg = (org) => {
    setSelectedOrgId(org.id);
    setSelectedCompanyId(org.companyId || null);
    rememberRecent(org);
  };

  return (
    <div className="IntegrationsPageContainer integrations-ops-page">
      <ToastContainer position="top-right" autoClose={3500} />

      <div className="integrations-page-header">
        <div>
          <p className="integrations-eyebrow">Integrations</p>
          <h1 className="integrations-page-title">{integration.title}</h1>
          <p className="integrations-page-subtitle">{integration.subtitle}</p>
        </div>
        <EnvToggle env={env} onChange={setEnv} />
      </div>

      <div className="integrations-tabs-bar" role="tablist">
        {INTEGRATIONS.map((integration) => (
          <button
            key={integration.id}
            type="button"
            role="tab"
            aria-selected={selectedIntegration === integration.id}
            className={`integrations-tab-pill ${
              selectedIntegration === integration.id ? "active" : ""
            }`}
            disabled={!integration.available}
            onClick={() => setSelectedIntegration(integration.id)}
          >
            {integration.img ? (
              <img
                src={integration.img}
                alt=""
                className="integrations-tab-logo"
                onError={(event) => {
                  event.currentTarget.style.visibility = "hidden";
                }}
              />
            ) : null}
            {integration.name}
          </button>
        ))}
      </div>

      <div className="integrations-workspace">
        {selectedIntegration === "encompass" ? (
          <>
            <EncompassOrgList
              env={env}
              selectedOrgId={selectedOrgId}
              onSelectOrg={handleSelectOrg}
            />
            <EncompassPanel
              env={env}
              organizationId={selectedOrgId}
              onClose={() => setSelectedOrgId(null)}
            />
          </>
        ) : (
          <>
            <FleetTable
              env={env}
              selectedOrgId={selectedOrgId}
              selectedCompanyId={selectedCompanyId}
              refreshKey={refreshKey}
              recents={recents}
              onSelectOrg={handleSelectOrg}
            />
            <DetailPanel
              env={env}
              organizationId={selectedOrgId}
              companyId={selectedCompanyId}
              onSelectCompany={setSelectedCompanyId}
              onLoaded={(org) => rememberRecent(org)}
              onClose={() => setSelectedOrgId(null)}
              onUpdated={() => setRefreshKey((value) => value + 1)}
            />
          </>
        )}
      </div>
    </div>
  );
}

export default IntegrationsPage;
